import React from 'react';
import PropTypes from 'prop-types';
import { X, ChevronDown, ChevronUp, Copy, Bookmark } from 'lucide-react';
import { getContainerColor } from '../utils/colors';
import { formatTimestamp } from '../utils/helpers';

const LogDrawer = ({ log, isOpen, onClose, onToggle, isBookmarked, onToggleBookmark, timestampMode }) => {
  if (!log) return null;

  const formattedTimestamp = formatTimestamp(log.timestamp, timestampMode);

  const handleCopy = () => {
    navigator.clipboard.writeText(log.message);
  };

  return (
    <>
      {/* Drawer */}
      <div
        className={`fixed left-0 right-0 bg-slate-800 border-t border-slate-700 shadow-2xl z-50 transition-all duration-300 ease-in-out ${
          isOpen ? 'bottom-0' : '-bottom-full'
        }`}
        style={{ maxHeight: '40vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700 bg-slate-900">
          <div className="flex items-center gap-3">
            <button
              onClick={onToggle}
              className="p-1 hover:bg-slate-700 rounded transition-colors"
              title={isOpen ? "Minimize" : "Maximize"}
            >
              {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
            <span className="text-sm font-medium text-slate-300">Log Line Details</span>
            <span className="text-xs text-slate-500">#{log.id}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToggleBookmark(log.id)}
              className={`p-1.5 rounded transition-colors ${
                isBookmarked ? 'text-yellow-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-700 hover:text-slate-300'
              }`}
              title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark size={16} fill={isBookmarked ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={handleCopy}
              className="p-1.5 hover:bg-slate-700 rounded transition-colors text-slate-400 hover:text-slate-200"
              title="Copy message"
            >
              <Copy size={16} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700 rounded transition-colors text-slate-400 hover:text-slate-200"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        {isOpen && (
          <div className="p-4 overflow-y-auto font-mono text-sm" style={{ maxHeight: 'calc(40vh - 48px)' }}>
            {/* Metadata */}
            <div className="flex flex-wrap gap-4 mb-3 pb-3 border-b border-slate-700">
              {formattedTimestamp && (
                <div>
                  <span className="text-xs text-slate-500 uppercase">Timestamp</span>
                  <div className="text-slate-300">{formattedTimestamp}</div>
                </div>
              )}
              <div>
                <span className="text-xs text-slate-500 uppercase">Container</span>
                <div className={getContainerColor(log.container)}>{log.container}</div>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase">Line</span>
                <div className="text-slate-300">#{log.id}</div>
              </div>
            </div>

            {/* Message */}
            <div>
              <span className="text-xs text-slate-500 uppercase mb-2 block">Message</span>
              <div className="text-slate-200 whitespace-pre-wrap break-all bg-slate-900 p-3 rounded border border-slate-700">
                {log.message}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

LogDrawer.propTypes = {
  log: PropTypes.shape({
    id: PropTypes.number.isRequired,
    container: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  isBookmarked: PropTypes.bool.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
  timestampMode: PropTypes.string.isRequired
};

export default LogDrawer;

